/**
 * DriveZone — AppLayout
 * Persistent split-screen shell for /app/* routes.
 * Replaces AppPlaceholder from Task 3.
 *
 * Desktop: Left 60% = ProximitySearch | Right 40% = ConfirmDistance
 * Mobile:  Tabs switching between the two panels
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, LogOut, Loader2, Crown, Search, Navigation, User as UserIcon, Gift } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useSearchStore } from '@/stores/searchStore'
import QuotaModal from '@/components/QuotaModal'
import NotificationBell from '@/components/NotificationBell'
import ProximitySearch from '@/pages/ProximitySearch'
import ConfirmDistance from '@/pages/ConfirmDistance'
import api from '@/lib/api'

type Tab = 'proximity' | 'confirm'

export default function AppLayout() {
  const navigate               = useNavigate()
  const { user, logout }       = useAuthStore()
  const showQuotaModal         = useSearchStore((s) => s.showQuotaModal)
  const [activeTab, setActiveTab] = useState<Tab>('proximity')
  const [loggingOut, setLoggingOut] = useState(false)

  async function handleLogout() {
    setLoggingOut(true)
    try { await api.post('/api/auth/logout/') } finally {
      logout()
      navigate('/login', { replace: true })
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">

          {/* Brand + Refer & Earn */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-primary-600 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-slate-900 text-base">DriveZone</span>
            </div>

            {/* Refer & Earn pill — always visible, links to account referral section */}
            <button
              onClick={() => navigate('/account#refer')}
              className="flex items-center gap-1.5 text-xs font-semibold
                         text-primary-700 bg-primary-50 border border-primary-200
                         rounded-full px-3 py-1.5
                         hover:bg-primary-100 hover:border-primary-300
                         transition-colors"
            >
              <Gift className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Refer &amp; Earn</span>
            </button>
          </div>

          {/* User info + logout */}
          <div className="flex items-center gap-3">
            <NotificationBell />
            {user?.is_subscribed && (
              <span className="hidden sm:flex items-center gap-1 text-xs font-medium
                               text-amber-600 bg-amber-50 border border-amber-200
                               rounded-full px-2.5 py-1">
                <Crown className="w-3 h-3" /> Pro
              </span>
            )}
            {user && !user.is_subscribed && (
              <button
                onClick={() => navigate('/subscribe')}
                className="hidden sm:flex items-center gap-1 text-xs font-medium
                           text-primary-600 hover:text-primary-700 transition-colors"
              >
                Upgrade to Pro
              </button>
            )}
            <button
              onClick={() => navigate('/account')}
              className="hidden sm:flex items-center gap-1 btn-secondary text-xs py-1.5 px-3"
              title={user?.email}
            >
              <UserIcon className="w-3.5 h-3.5" />
              <span className="hidden md:inline truncate max-w-28">{user?.email?.split('@')[0]}</span>
            </button>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              {loggingOut
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <><LogOut className="w-3.5 h-3.5" /><span className="hidden sm:inline">Sign out</span></>
              }
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile tab switcher ─────────────────────────────────── */}
      <div className="flex-shrink-0 sm:hidden bg-white border-b border-slate-200">
        <div className="flex">
          {([
            { id: 'proximity', label: 'Find Nearby',   icon: Search },
            { id: 'confirm',   label: 'Confirm Dist.', icon: Navigation },
          ] as const).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium
                          border-b-2 transition-colors
                          ${activeTab === id
                            ? 'border-primary-600 text-primary-700'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                          }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────── */}
      <main className="flex-1 min-h-0 max-w-6xl mx-auto w-full px-4 py-4 sm:py-6">

        {/* Desktop: side-by-side */}
        <div className="hidden sm:flex gap-5 h-[calc(100vh-8rem)]">
          {/* Left panel — 60% */}
          <div className="flex-[3] card p-5 overflow-hidden flex flex-col min-h-0">
            <ProximitySearch />
          </div>
          {/* Right panel — 40% */}
          <div className="flex-[2] card p-5 overflow-hidden flex flex-col min-h-0">
            <ConfirmDistance />
          </div>
        </div>

        {/* Mobile: single panel via tabs */}
        <div className="sm:hidden h-[calc(100vh-10rem)] card p-4 overflow-hidden flex flex-col">
          {activeTab === 'proximity' ? <ProximitySearch /> : <ConfirmDistance />}
        </div>
      </main>

      {/* Quota modal — rendered here so it overlays the whole layout */}
      {showQuotaModal && <QuotaModal />}
    </div>
  )
}
